import { createContext, ReactNode, useContext, useRef } from 'react';

interface HiddenFormPayload {
  registerForm: (ref: HTMLFormElement | null) => void;
  submitForm: () => void;
}

const defaultHiddenFormPayload: HiddenFormPayload = {
  registerForm: () => {
    throw new Error(
      'registerForm function must be used within a HiddenFormProvider',
    );
  },
  submitForm: () => {
    throw new Error(
      'submitForm function must be used within a HiddenFormProvider',
    );
  },
};

const HiddenFormContext = createContext<HiddenFormPayload>(
  defaultHiddenFormPayload,
);

export const HiddenFormProvider = ({ children }: { children: ReactNode }) => {
  const formRef = useRef<HTMLFormElement | null>(null);
  const pendingAction = useRef<(() => void) | null>(null);
  const registerForm = (ref: HTMLFormElement | null) => {
    formRef.current = ref;

    if (ref && pendingAction.current) {
      pendingAction.current();
      pendingAction.current = null;
    }
  };

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submit();
    } else {
      pendingAction.current = () => formRef.current?.submit();
    }
  };

  return (
    <HiddenFormContext.Provider value={{ registerForm, submitForm }}>
      {children}
    </HiddenFormContext.Provider>
  );
};

export const useHiddenFormContext = () => useContext(HiddenFormContext);
